import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import howToBuy from '../../assets/how_to_buy.png'; 
import howToSell from '../../assets/how_to_sell.png';
import EscrowProtection from '../../assets/escrow_protection.png';

const MobileOptimizedImageCarousel = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const slides = [
    {
      id: 1,
      buttonText: "Buying Guide",
      action: () => navigate('/how-to-buy'),
      image: howToBuy,
    },
    {
      id: 2,
      buttonText: "Selling Guide",
      action: () => navigate('/how-to-sell'),
      image: howToSell,
    },
    {
      id: 3,
      buttonText: "Learn Escrow",
      action: () => navigate('/escrow'),
      image: EscrowProtection,
    },
    // {
    //   id: 4,
    //   buttonText: "Our Story",
    //   action: () => navigate('/about-us'),
    //   image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
    // }
  ];

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    const slideWidth = carouselRef.current.offsetWidth;
    const newIndex = Math.round(carouselRef.current.scrollLeft / slideWidth);
    setCurrentSlide(Math.max(0, Math.min(newIndex, slides.length - 1)));
  };

  const nextSlide = () => {
    const nextIndex = (currentSlide + 1) % slides.length;
    setCurrentSlide(nextIndex);
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: nextIndex * carouselRef.current.offsetWidth,
        behavior: 'smooth'
      });
    }
  };

  const prevSlide = () => {
    const prevIndex = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    setCurrentSlide(prevIndex);
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: prevIndex * carouselRef.current.offsetWidth,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: currentSlide * carouselRef.current.offsetWidth,
        behavior: 'smooth'
      });
    }
  }, [currentSlide]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [currentSlide]);

  return (
    <div className="relative w-full max-w-[95vw] mx-auto">
      <div 
        ref={carouselRef}
        className="flex overflow-x-auto snap-x snap-mandatory h-[250px] sm:h-[300px] md:h-[350px] rounded-xl sm:rounded-2xl shadow-lg"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={(e) => {
          setIsDragging(true);
          setStartX(e.touches[0].pageX - carouselRef.current.offsetLeft);
          setScrollLeft(carouselRef.current.scrollLeft);
        }}
        onTouchMove={(e) => {
          if (!isDragging) return;
          const x = e.touches[0].pageX - carouselRef.current.offsetLeft;
          const walk = (x - startX) * 2;
          carouselRef.current.scrollLeft = scrollLeft - walk;
        }}
        onTouchEnd={handleMouseUp}
        style={{ 
          cursor: isDragging ? 'grabbing' : 'grab',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <style>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        
        {slides.map((slide, index) => (
          <div 
            key={slide.id}
            className="flex-shrink-0 w-full h-full relative snap-center"
          >
            <img 
              src={slide.image} 
              alt=""
              className="absolute inset-0 w-full h-full object-fit"
            />
            
            <div className="absolute bottom-4 right-4 z-10">
              <button
                onClick={slide.action}
                className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-[#ff660e] text-white rounded-lg font-bold hover:bg-[#e55b0d] active:scale-95 transition-all shadow-lg hover:shadow-xl text-xs sm:text-sm"
              >
                {slide.buttonText}
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* <button
        onClick={prevSlide}
        className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 active:scale-95 transition-all z-20"
        aria-label="Previous slide"
      >
        ←
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 active:scale-95 transition-all z-20"
        aria-label="Next slide"
      >
        →
      </button> */}

      <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all w-2 h-2 sm:w-3 sm:h-3 rounded-full ${
              index === currentSlide 
                ? 'bg-white' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileOptimizedImageCarousel;